import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import {
  getStatusProgress,
  isTicketCompleted,
  getCategoryName,
  getTicketTitle,
  getDisplayDate,
  formatTicketDate,
} from '@/utils/residents/maintenance';
import { MaintenanceProgressBar } from '@/components/residents/maintenance/shared';

interface DesktopWorkRequestCardProps {
  ticket: IMaintenanceListResponse;
  onPress: () => void;
  width?: number;
}

/**
 * DesktopWorkRequestCard - Card component for displaying maintenance tickets on desktop
 * Matches Figma design with category label, date, title, and 3-segment status bar
 */
export const DesktopWorkRequestCard: React.FC<DesktopWorkRequestCardProps> = ({
  ticket,
  onPress,
  width,
}) => {
  const progress = getStatusProgress(ticket);
  const completed = isTicketCompleted(ticket);
  const categoryLabel = getCategoryName(ticket);
  const displayDate = formatTicketDate(getDisplayDate(ticket), 'medium', '');
  const title = getTicketTitle(ticket);

  return (
    <TouchableOpacity
      style={[styles.card, width ? { width } : undefined]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      {/* Header Row - Category Label + Date */}
      <View style={styles.headerRow}>
        <View style={styles.categoryLabel}>
          <Text style={styles.categoryLabelText}>{categoryLabel}</Text>
        </View>
        <Text style={styles.dateText}>{displayDate}</Text>
      </View>

      {/* Title */}
      <Text style={styles.title} numberOfLines={2}>
        {title}
      </Text>

      {/* Footer Row - Progress Bar + Icon */}
      <View style={styles.footerRow}>
        {/* 3-Segment Progress Bar */}
        <MaintenanceProgressBar progress={progress} variant="desktop" />

        {/* Status Icon */}
        {completed ? (
          <View style={styles.completedIcon}>
            <CheckIcon />
          </View>
        ) : (
          <View style={styles.wrenchIcon}>
            <RESIDENTS_SVG_COMPONENTS.WRENCH width={17} height={17} color={PRIMITIVE_COLORS.gray500} />
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

// Simple checkmark icon component
const CheckIcon: React.FC = () => (
  <View style={styles.checkIconContainer}>
    <View style={styles.checkmark}>
      <View style={styles.checkmarkShort} />
      <View style={styles.checkmarkLong} />
    </View>
  </View>
);

const styles = StyleSheet.create({
  card: {
    backgroundColor: PRIMITIVE_COLORS.gray900,
    borderRadius: 16,
    padding: 20,
    gap: 14,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && ({
      cursor: 'pointer',
    } as any)),
  },

  // Header Row
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  categoryLabel: {
    backgroundColor: PRIMITIVE_COLORS.white10,
    borderRadius: 60,
    paddingHorizontal: 10,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryLabelText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 18,
  },
  dateText: {
    fontSize: 12,
    fontWeight: '400',
    color: PRIMITIVE_COLORS.gray400,
    lineHeight: 18,
  },

  // Title
  title: {
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },

  // Footer Row
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 21,
  },

  // Icons
  wrenchIcon: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  completedIcon: {
    width: 21,
    height: 21,
    borderRadius: 10.5,
    backgroundColor: PRIMITIVE_COLORS.greenBg10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkIconContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkmark: {
    width: 11.25,
    height: 8,
    position: 'relative',
  },
  checkmarkShort: {
    position: 'absolute',
    width: 2,
    height: 5,
    backgroundColor: PRIMITIVE_COLORS.successGreen,
    bottom: 0,
    left: 2,
    transform: [{ rotate: '-45deg' }],
    borderRadius: 1,
  },
  checkmarkLong: {
    position: 'absolute',
    width: 2,
    height: 8,
    backgroundColor: PRIMITIVE_COLORS.successGreen,
    bottom: 0,
    right: 2,
    transform: [{ rotate: '45deg' }],
    borderRadius: 1,
  },
});

export default DesktopWorkRequestCard;
